import { Download, FileText, LinkIcon } from 'lucide-react';
import { useParams } from 'react-router-dom';

import {
  getDocumentosPublicoControllerPdfUrl,
  useDocumentosPublicoControllerObter,
} from '@/api/generated/documentos/documentos';
import { urlDeArquivo } from '@/api/http-client';
import { EstadoCarregando, EstadoVazio } from '@/shared/components/estados';
import { LogoNohra } from '@/shared/components/LogoNohra';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';

const ROTULO_DO_TIPO: Record<string, string> = {
  RECEITUARIO: 'Receituário',
  REQUISICAO: 'Requisição de exames',
};

/**
 * A página que o link compartilhado abre — a ponta que faltava.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  Quem chega aqui é o TUTOR, não um usuário do sistema: sem conta, sem
 *  sessão, quase sempre no celular, vindo de uma mensagem. Por isso a tela não
 *  tem sidebar, não tem header do app e não oferece nenhum caminho para dentro
 *  do sistema — o token na URL autoriza este documento e mais nada.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * O erro é UM só, deliberadamente vago ("não é válido ou expirou"): o backend
 * responde igual para token inexistente, revogado e vencido, e a tela não pode
 * ser mais específica do que a resposta que recebeu.
 */
export function DocumentoCompartilhadoPage() {
  const { token = '' } = useParams<{ token: string }>();

  const { data, isPending, isError } = useDocumentosPublicoControllerObter(token, {
    query: {
      enabled: Boolean(token),
      // Link expirado não melhora com insistência, e cada tentativa consome a
      // cota do throttler da rota pública.
      retry: false,
    },
  });

  const urlDoPdf = urlDeArquivo(getDocumentosPublicoControllerPdfUrl(token));

  return (
    <div className="min-h-screen bg-muted/30 flex flex-col">
      <header className="border-b bg-surface-raised">
        <div className="mx-auto max-w-4xl px-4 py-3 flex justify-center">
          <LogoNohra tamanho="sm" />
        </div>
      </header>

      <main className="flex-1 mx-auto w-full max-w-4xl p-4 space-y-4">
        {isPending && <EstadoCarregando rotulo="Abrindo o documento..." />}

        {isError && (
          <EstadoVazio
            icone={LinkIcon}
            titulo="Este link não é válido ou expirou"
            descricao="Peça um link novo à clínica que emitiu o documento."
          />
        )}

        {data && (
          <>
            <Card>
              <CardContent className="p-4 space-y-1">
                <p className="text-sm font-medium text-brand">
                  {ROTULO_DO_TIPO[data.tipo] ?? 'Documento'}
                  {data.numero ? ` nº ${data.numero}` : ''}
                </p>
                <h1 className="text-lg font-semibold">{data.pacienteNome}</h1>
                <p className="text-sm text-muted-foreground">
                  {data.veterinarioNome}
                  {data.clinica ? ` — ${data.clinica}` : ''}
                </p>
                {data.assinadoEm && (
                  <p className="text-sm text-muted-foreground">
                    {`Emitido em ${new Date(data.assinadoEm).toLocaleDateString('pt-BR')}.`}
                  </p>
                )}

                <div className="pt-3">
                  {/* `download` sozinho não basta: a resposta vem com
                      `Content-Disposition: inline` para o PDF abrir embutido
                      acima, então o atributo é o que diferencia baixar de ver. */}
                  <Button asChild variant="outline" size="sm">
                    <a href={urlDoPdf} download>
                      <Download className="w-4 h-4 mr-2" />
                      Baixar PDF
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* `<object>` e não `<iframe>`: dá um fallback de verdade para o
                navegador que não sabe exibir PDF — no iOS antigo e em vários
                in-app browsers (inclusive o do WhatsApp, que é justamente por
                onde este link chega) o iframe renderiza uma área em branco sem
                oferecer saída. */}
            <object
              data={urlDoPdf}
              type="application/pdf"
              className="w-full h-[70vh] rounded-lg border bg-surface-raised"
              aria-label={`${ROTULO_DO_TIPO[data.tipo] ?? 'Documento'} de ${data.pacienteNome}`}
            >
              <EstadoVazio
                icone={FileText}
                titulo="Seu navegador não exibe PDF nesta tela"
                descricao="Baixe o arquivo para abrir no leitor do seu aparelho."
                acao={
                  <Button asChild size="sm">
                    <a href={urlDoPdf} download>
                      <Download className="w-4 h-4 mr-2" />
                      Baixar PDF
                    </a>
                  </Button>
                }
              />
            </object>

            <p className="text-center text-xs text-muted-foreground">
              {`Este link é pessoal e vale até ${new Date(data.expiraEm).toLocaleString('pt-BR', {
                dateStyle: 'short',
                timeStyle: 'short',
              })}.`}
            </p>
          </>
        )}
      </main>
    </div>
  );
}
